// The classification regexes below match Spanish form wording, so most of these
// are fragments on purpose (`suscri` covers suscribite/suscripción/suscribirme).
// cspell:ignore acept bolet boletín condicion cuenta deseo novedades nueva ofertas privac privacidad política promoci recibir regist rminos subscri suscri suscribite Suscribirme suscripción términos
import { isValidEmail } from './email';

/**
 * Reads a `<form>` once and reports everything the optin pipeline needs:
 * the email it carries, what kind of form it is, and whether the visitor
 * ticked a consent checkbox.
 *
 * Spec §3.3 still holds — no field VALUE ever reaches an event property. The
 * email found here goes to `Identity.setEmail()` (hashed client-side); the
 * `context` bag this returns carries only form metadata and booleans.
 */

export type FormOptinSource = 'login' | 'register' | 'newsletter' | 'form';

/** Newsletter / subscription wording, ES + EN. */
const NEWSLETTER_RE = /news[\s_-]?letter|subscri|suscri|bolet[ií]n|boletin/i;

/** Account-creation wording, ES + EN. */
const REGISTER_RE = /regist|sign[\s_-]?up|crear[\s_-]?cuenta|create[\s_-]?account|nueva[\s_-]?cuenta/i;

/**
 * A checkbox whose wording makes it a consent / subscription opt-in. Covers the
 * legal phrasings ("acepto los términos", "política de privacidad") and the
 * marketing ones, which in Spanish are usually built around *recibir* something
 * — "quiero recibir novedades", "deseo recibir ofertas".
 */
const CONSENT_RE =
	/consent|acept|accept|t[eé]rminos|terminos|terms|condicion|privac|suscri|subscri|news[\s_-]?letter|opt[\s_-]?in|recibir|novedades|promoci|ofertas|marketing/i;

const EMAIL_NAME_RE = /e[-_]?mail/i;

/** Inputs a human types into. Drives the "this form only asks for an email"
 *  rule, so buttons, hidden fields and checkboxes don't count. */
const TEXT_INPUT_TYPES = new Set(['text', 'email', 'tel', 'url', 'search', 'number', 'password', '']);

export interface FormScanResult {
	/** Cleartext email found in the form, or null. Never leaves the browser. */
	email: string | null;
	source: FormOptinSource;
	/** Form metadata safe to put on the wire. */
	context: Record<string, unknown>;
}

function looksEmailLikeName(el: HTMLInputElement): boolean {
	if (el.autocomplete === 'email') return true;
	return EMAIL_NAME_RE.test(el.name || '') || EMAIL_NAME_RE.test(el.id || '');
}

/** Text near a checkbox: its `<label for>`, an ancestor `<label>`, plus the
 *  attributes the developer named it with. */
function checkboxWording(el: HTMLInputElement, form: HTMLFormElement): string {
	let text = `${el.name || ''} ${el.id || ''} ${typeof el.className === 'string' ? el.className : ''}`;
	try {
		if (el.id) {
			const labelled = form.querySelector(`label[for="${el.id}"]`);
			if (labelled) text += ' ' + (labelled.textContent || '');
		}
		const ancestor = el.closest('label');
		if (ancestor) text += ' ' + (ancestor.textContent || '');
	} catch {
		/* exotic id that breaks the selector — the attributes above still count */
	}
	return text;
}

/** Form-level wording used to classify: the attributes plus the submit label,
 *  which is often the only place "Suscribirme" appears. */
function formWording(form: HTMLFormElement): string {
	const parts = [
		form.id || '',
		form.getAttribute('name') || '',
		typeof form.className === 'string' ? form.className : '',
		form.getAttribute('action') || '',
		form.getAttribute('data-emblue-form') || '',
	];
	try {
		const submit = form.querySelector('button[type="submit"], input[type="submit"], button:not([type])');
		if (submit) {
			parts.push((submit as HTMLElement).innerText || submit.textContent || '');
			parts.push((submit as HTMLInputElement).value || '');
		}
	} catch {
		/* querySelector can't fail on a real form, but the SDK never throws here */
	}
	return parts.join(' ');
}

export function scanForm(form: HTMLFormElement): FormScanResult {
	const inputs = form.querySelectorAll<HTMLInputElement>('input');

	let email: string | null = null;
	let emailFallback: string | null = null;
	let passwordCount = 0;
	let textInputCount = 0;
	let consentPresent = false;
	let consentChecked = false;

	for (let i = 0; i < inputs.length; i++) {
		const el = inputs[i];
		const type = (el.type || '').toLowerCase();

		if (type === 'password') {
			passwordCount++;
			textInputCount++;
			continue;
		}

		if (type === 'checkbox') {
			if (CONSENT_RE.test(checkboxWording(el, form))) {
				consentPresent = true;
				if (el.checked) consentChecked = true;
			}
			continue;
		}

		if (type !== 'hidden' && TEXT_INPUT_TYPES.has(type)) textInputCount++;

		const value = (el.value || '').trim();
		if (!value) continue;
		if (email) continue;
		if (type === 'email' && isValidEmail(value)) {
			email = value;
		} else if (looksEmailLikeName(el) && isValidEmail(value)) {
			email = value;
		} else if (!emailFallback && type === 'text' && isValidEmail(value)) {
			emailFallback = value;
		}
	}

	// `select` and `textarea` count toward "how much is this form asking for",
	// so a name + email + message contact form isn't read as a newsletter.
	textInputCount += form.querySelectorAll('textarea, select').length;

	const context: Record<string, unknown> = {
		form_id: form.id || undefined,
		form_name: form.getAttribute('name') || undefined,
		form_action: form.getAttribute('action') || undefined,
		consent_checkbox: consentPresent ? (consentChecked ? 'checked' : 'unchecked') : undefined,
	};

	return {
		email: email || emailFallback,
		source: classify(formWording(form), passwordCount, textInputCount, !!(email || emailFallback)),
		context,
	};
}

/**
 * Password fields decide first: a login page can easily mention "suscribite"
 * somewhere in its markup, and mistaking a login for a newsletter opt-in is the
 * one error with a legal edge to it.
 */
function classify(wording: string, passwordCount: number, textInputCount: number, hasEmail: boolean): FormOptinSource {
	if (passwordCount > 0) {
		// Two password fields is the confirm-password pattern; nobody asks a
		// returning user to type their password twice.
		return passwordCount >= 2 || REGISTER_RE.test(wording) ? 'register' : 'login';
	}
	if (NEWSLETTER_RE.test(wording)) return 'newsletter';
	if (REGISTER_RE.test(wording)) return 'register';
	// A form whose only question is "your email?" is a subscription box, whatever
	// the site called it.
	if (hasEmail && textInputCount <= 1) return 'newsletter';
	return 'form';
}
